import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import CookieConsent from "@/components/MeridianConsent";
import "@/styles/meridian-pages.css";
import "@/styles/system.css";


// Global error boundary — prevents any render crash from leaving a blank screen
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("[GlobalSync] Render error caught by ErrorBoundary:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: "#f4efe6", color: "#0e2a1f", fontFamily: "DM Sans, sans-serif",
          textAlign: "center", padding: "2rem"
        }}>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>Something went wrong</h1>
          <p style={{ color: "#526659", marginBottom: "2rem" }}>
            An unexpected error occurred. Please refresh the page to continue.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.75rem 2rem", borderRadius: "0.75rem",
              background: "#c8a96a", color: "#071a0e",
              fontWeight: "700", border: "none", cursor: "pointer", fontSize: "1rem"
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Static import for instant above-the-fold homepage rendering
import LandingPage from "@/pages/LandingPage";

// Lazy imports for all other pages to split JS bundles
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const TimeZoneConverterPage = React.lazy(() => import("@/pages/TimeZoneConverterPage"));
const CurrencyConverterPage = React.lazy(() => import("@/pages/CurrencyConverterPage"));
const MeetingPlannerPage = React.lazy(() => import("@/pages/MeetingPlannerPage"));
const AdminPage = React.lazy(() => import("@/pages/AdminPage"));
const AboutPage = React.lazy(() => import("@/pages/AboutPage"));
const ContactPage = React.lazy(() => import("@/pages/ContactPage"));
const PrivacyPolicyPage = React.lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = React.lazy(() => import("@/pages/TermsOfServicePage"));
const EditorialPolicyPage = React.lazy(() => import("@/pages/EditorialPolicyPage"));
const MethodologyPage = React.lazy(() => import("@/pages/MethodologyPage"));
const GuidesPage = React.lazy(() => import("@/pages/GuidesPage"));
const GuidePage = React.lazy(() => import("@/pages/GuidePage"));
const LegacyBlogRedirect = React.lazy(() => import("@/pages/LegacyBlogRedirect"));
const CityPairPage = React.lazy(() => import("@/pages/CityPairPage"));
const CurrencyPairPage = React.lazy(() => import("@/pages/CurrencyPairPage"));
const DataSourcesPage = React.lazy(() => import("@/pages/DataSourcesPage"));
const FreelancerRateConverterPage = React.lazy(() => import("@/pages/FreelancerRateConverterPage"));
const RemoteTeamsMeetingPlannerPage = React.lazy(() => import("@/pages/RemoteTeamsMeetingPlannerPage"));
const USIndiaMeetingTimePage = React.lazy(() => import("@/pages/USIndiaMeetingTimePage"));
const TeamWorkspacePage = React.lazy(() => import("@/pages/TeamWorkspacePage"));
const InvoicePage = React.lazy(() => import("@/pages/InvoicePage"));
const StripeCheckoutSimulatorPage = React.lazy(() => import("@/pages/PlansPage"));
const UpgradeSuccessPage = React.lazy(() => import("@/pages/PlansPage"));
const NotFoundPage = React.lazy(() => import("@/pages/NotFoundPage"));
const PressPage = React.lazy(() => import("@/pages/PressPage"));
const AuthorPage = React.lazy(() => import("@/pages/AuthorPage"));
const ConvertHubPage = React.lazy(() => import("@/pages/ConvertHubPage"));
const ZonePairPage = React.lazy(() => import("@/pages/ZonePairPage"));
const FreelanceRateHubPage = React.lazy(() => import("@/pages/FreelanceRateHubPage"));
const FreelanceRatePage = React.lazy(() => import("@/pages/FreelanceRatePage"));
const MeetingOverlapHubPage = React.lazy(() => import("@/pages/MeetingOverlapHubPage"));
const MeetingOverlapPage = React.lazy(() => import("@/pages/MeetingOverlapPage"));
const ComparisonPage = React.lazy(() => import("@/pages/ComparisonPage"));
const WorldClockWidgetPage = React.lazy(() => import("@/pages/WorldClockWidgetPage"));
const EmbedPage = React.lazy(() => import("@/pages/EmbedPage"));
const EmbedWorldClock = React.lazy(() => import("@/pages/EmbedWorldClock"));





const SuspenseFallback = () => (
  <div role="status" className="meridian-loading">Opening your workspace…</div>
);

function PageTheme({ children }) {
  const { pathname } = useLocation();
  return <div className={pathname === '/' ? 'meridian-root' : 'meridian-interior'}>{children}</div>;
}

// The embed is shown inside other sites' iframes: no cookie banner there.
function SiteConsent() {
  const { pathname } = useLocation();
  return pathname.startsWith("/embed/") ? null : <CookieConsent />;
}

function RoutePosition() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => { if (!hash) window.scrollTo({top:0,left:0,behavior:'instant'}); }, [pathname, hash]);
  return null;
}
function App() {
  React.useEffect(() => {
    // Preload main route chunks after 1s idle time so single-click button navigation responds instantly
    const timer = setTimeout(() => {
      import("@/pages/Dashboard");
      import("@/pages/TimeZoneConverterPage");
      import("@/pages/CurrencyConverterPage");
      import("@/pages/MeetingPlannerPage");
      import("@/pages/FreelancerRateConverterPage");
      import("@/pages/InvoicePage");
      import("@/pages/GuidesPage");
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <RoutePosition />
          <PageTheme>
          <React.Suspense fallback={<SuspenseFallback />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/time-zone-converter" element={<TimeZoneConverterPage />} />
              <Route path="/currency-converter" element={<CurrencyConverterPage />} />
              <Route path="/meeting-planner" element={<MeetingPlannerPage />} />
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/guides/:slug" element={<GuidePage />} />
              <Route path="/blog" element={<Navigate to="/guides" replace />} />
              <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
              <Route path="/time/:pair" element={<CityPairPage />} />
              <Route path="/currency/:pair" element={<CurrencyPairPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/press" element={<PressPage />} />
              <Route path="/authors/:slug" element={<AuthorPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
              <Route path="/methodology" element={<MethodologyPage />} />
              <Route path="/data-sources" element={<DataSourcesPage />} />
              <Route path="/freelancer-rate-converter" element={<FreelancerRateConverterPage />} />
              <Route path="/freelancer-rate-calculator" element={<Navigate to="/freelancer-rate-converter" replace />} />
              <Route path="/global-meeting-planner-for-remote-teams" element={<RemoteTeamsMeetingPlannerPage />} />
              <Route path="/us-india-meeting-time" element={<USIndiaMeetingTimePage />} />
              <Route path="/team/:slug" element={<TeamWorkspacePage />} />
              <Route path="/invoice" element={<InvoicePage />} />
              <Route path="/stripe-checkout" element={<StripeCheckoutSimulatorPage />} />
              <Route path="/upgrade-success" element={<UpgradeSuccessPage />} />
              <Route path="/admin" element={<AdminPage />} />
              {/* Timezone abbreviation pair converters (PR 2) */}
              <Route path="/convert" element={<ConvertHubPage />} />
              <Route path="/convert/:pair" element={<ZonePairPage />} />
              {/* Freelance Rate Currency Corridors (PR 4) */}
              <Route path="/freelance-rate" element={<FreelanceRateHubPage />} />
              <Route path="/freelance-rate/:corridor" element={<FreelanceRatePage />} />
              {/* Meeting Overlap Guides (PR 5) */}
              <Route path="/meeting-overlap" element={<MeetingOverlapHubPage />} />
              <Route path="/meeting-overlap/:corridor" element={<MeetingOverlapPage />} />
              {/* Tool Comparisons (PR 6) */}
              <Route path="/compare/:slug" element={<ComparisonPage />} />
              <Route path="/world-clock-widget" element={<WorldClockWidgetPage />} />
              <Route path="/embed" element={<EmbedPage />} />
              <Route path="/embed/world-clock" element={<EmbedWorldClock />} />
              <Route path="/download" element={<Navigate to="/world-clock-widget" replace />} />
              <Route path="*" element={<NotFoundPage />} />

            </Routes>
          </React.Suspense>
          </PageTheme>
          <Toaster position="top-right" richColors />
          <SiteConsent />
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
